import { Suspense, lazy } from 'react';
import { Navigate, Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import RootLayout from 'components/RootLayout';
import DefaultLandingRedirect from 'components/shared/DefaultLandingRedirect';
import RequireApp from 'components/shared/RequireApp';
import RequireFeatureFlag from 'components/shared/RequireFeatureFlag';
import { ThemeProvider } from 'components/shared/ThemeProvider';
import { queryClient } from 'contexts/QueryClient';
import { AuthProvider } from 'pages/AuthProvider';
import ErrorPage from 'pages/ErrorPage';
import Home from 'pages/Home';
import { UserProvider } from 'pages/UserProvider';
import {
  aceAutoFollowUp,
  aceComingSoon,
  aceConversations,
  aceSettings,
  aceTeach,
  aceToday,
  calendar,
  companyDetails,
  defaultSettings,
  emailSentRoute,
  getCalendarLocationRoute,
  getHeroLocationSettingRoute,
  heroBusinessSettings,
  heroInsights,
  heroProspects,
  heroSettings,
  loginRoute,
  miaAdCopywriter,
  miaCanonryDashboard,
  miaGoogleAiSearch,
  miaMetaAds,
  miaSettings,
  miaTeach,
  miaToday,
  miaVideoAds,
  miaVideoAdsSuccess,
  newPassword,
  plansLanding,
  presentations,
  presentationsDefaultPath,
  productAnalyticsDashboard,
  resetPasswordRoute,
  rootRoute,
  settings,
  workflowLanding,
} from 'routes/routes';

const Members = lazy(() => import('pages/hero/Members'));
const Teams = lazy(() => import('pages/hero/Teams'));
const CompanySettings = lazy(() => import('pages/company/CompanyLanding'));
const CompanySubscriptions = lazy(() => import('pages/company/CompanySubscriptions'));
const CompanyApiKeys = lazy(() => import('pages/company/CompanyApiKeys'));
const CompanyDetails = lazy(() => import('pages/company/CompanyDetails'));
const CompanyListing = lazy(() => import('pages/company/CompanyListing'));
const CompanyPlan = lazy(() => import('pages/company/CompanyPlan'));
const IncomingWebhooks = lazy(() => import('pages/company/IncomingWebhooks'));
const OutgoingWebhooks = lazy(() => import('pages/company/OutgoingWebhooks'));
const Prospects = lazy(() => import('pages/hero/Prospects'));
const TerritoriesListing = lazy(() => import('pages/hero/territories/TerritoriesListing'));
const TerritoryLanding = lazy(() => import('pages/hero/territories/TerritoryLanding'));
const TeamManagement = lazy(() => import('pages/hero/TeamManagement'));
const BusinessSettings = lazy(() => import('pages/hero/BusinessSettings'));
const MemberProfile = lazy(() => import('pages/hero/MemberProfile'));
const Dashboards = lazy(() => import('pages/hero/Dashboards'));
const Main = lazy(() => import('pages/hero/Main'));
const HeroSettingsLanding = lazy(() => import('pages/hero/heroSettings/HeroSettingsLanding'));
const HeroDefaultSettings = lazy(() => import('pages/hero/heroSettings/HeroDefaultSettings'));
const HeroTerritorySettings = lazy(() => import('pages/hero/heroSettings/HeroTerritorySettings'));
const CalendarSettingsLanding = lazy(() => import('pages/calendarSettings/CalendarSettingsLanding'));
const CalendarDefaultSettings = lazy(() => import('pages/calendarSettings/CalendarDefaultSettings'));
const CalendarTerritorySettings = lazy(() => import('pages/calendarSettings/CalendarTerritorySettings'));
const ProfileLanding = lazy(() => import('pages/profile/ProfileLanding'));
const ProfileSettings = lazy(() => import('pages/profile/ProfileDetails'));
const ChangePassword = lazy(() => import('pages/profile/ChangePassword'));
const NewPassword = lazy(() => import('pages/ActivateAccount'));
const ResetPassword = lazy(() => import('pages/ResetPassword'));
const ResetPasswordSent = lazy(() => import('pages/ResetPasswordSent'));
const Login = lazy(() => import('pages/Login'));
const AdCopywriter = lazy(() => import('pages/mia/AdCopywriter'));
const CanonryDashboard = lazy(() => import('pages/mia/CanonryDashboard'));
const GoogleAiSearch = lazy(() => import('pages/mia/GoogleAiSearch'));
const AceComingSoon = lazy(() => import('pages/ace/AceComingSoon'));
const AIVideoAds = lazy(() => import('pages/aiVideoAds/AIVideoAds'));
const AIVideoAdsSuccessPage = lazy(() => import('pages/aiVideoAds/AIVideoAdsSuccess'));
const Integrations = lazy(() => import('pages/integrations/Integrations'));
const IntegrationEvents = lazy(() => import('pages/integrations/IntegrationEvents'));
const OAuthRedirect = lazy(() => import('pages/OAuthRedirect'));
const CreateWorkflow = lazy(() => import('pages/workflow/CreateWorkflow'));
const WorkflowEditor = lazy(() => import('pages/workflow/WorkflowEditor'));
const WorkflowEditorLanding = lazy(() => import('pages/workflow/WorkflowEditorLanding'));
const EmbeddedLanding = lazy(() => import('pages/workflow/EmbeddedLanding'));
const WorkflowThemeTab = lazy(() => import('pages/workflow/WorkflowTheme'));
const WorkflowTracking = lazy(() => import('pages/workflow/WorkflowTracking'));
const WorkflowAnalytics = lazy(() => import('pages/workflow/WorkflowAnalytics'));
const WorkflowSendEmail = lazy(() => import('pages/workflow/WorkflowSendEmail'));
const WorkflowSendHistory = lazy(() => import('pages/workflow/WorkflowSendHistory'));
const SettingLanding = lazy(() => import('pages/hero/settings/SettingsLanding'));
const Settings = lazy(() => import('pages/hero/settings/Settings'));
const PlanLanding = lazy(() => import('pages/plans/PlansLanding'));
const Organizations = lazy(() => import('pages/organization/OrganizationLanding'));
const ProductAnalyticsDashboard = lazy(() => import('pages/productAnalytics/ProductAnalyticsDashboard'));
const WorkflowLanding = lazy(() => import('pages/workflow/WorkflowLanding'));
const FolderDetailPage = lazy(() => import('pages/workflow/FolderDetails'));
const NotFound = lazy(() => import('pages/NotFound'));
const MarketingResources = lazy(() => import('pages/MarketingResources'));
const Presentations = lazy(() => import('pages/presentations/Presentations'));
const PlaceholderPage = lazy(() => import('pages/PlaceholderPage'));

const LazyPage = ({ page }: { page: JSX.Element }): JSX.Element => (
  <Suspense
    fallback={
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin h-6 w-6 border-2 border-muted-foreground border-t-transparent rounded-full" />
      </div>
    }
  >
    {page}
  </Suspense>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      <Route
        path={rootRoute}
        element={
          <UserProvider>
            <Home />
          </UserProvider>
        }
        errorElement={<ErrorPage />}
      >
        <Route index element={<DefaultLandingRedirect />} />
        <Route path={miaVideoAds} element={<LazyPage page={<AIVideoAds />} />} />
        <Route path={miaVideoAdsSuccess} element={<LazyPage page={<AIVideoAdsSuccessPage />} />} />
        <Route path="integrations" element={<LazyPage page={<Integrations />} />} />
        <Route path="integrations/:type/events" element={<LazyPage page={<IntegrationEvents />} />} />
        <Route path="organizations" element={<LazyPage page={<Organizations />} />} />
        <Route path="oauth" element={<LazyPage page={<OAuthRedirect />} />} />

        <Route path="hero" element={<LazyPage page={<Main />} />}>
          <Route index element={<LazyPage page={<NotFound />} />} />
          {/* Leads stays ungated: leads arrive from Journeys as well as canvassing. */}
          <Route path={heroProspects} element={<LazyPage page={<Prospects />} />} />
          <Route element={<RequireApp clientId="hero" />}>
            <Route path={heroInsights} element={<LazyPage page={<Dashboards />} />} />
          </Route>
        </Route>
        {/*
          Ungated on purpose: BusinessSettings is generic company config (name, currency, logo,
          lead-notification emails) and is the "Set Up Business Settings" target of the empty
          state in TerritoriesListing, which renders in the hero, polaris AND calendar location
          trees. Gating it would dead-end first-run setup. Only its URL is hero-shaped.
        */}
        <Route path={heroBusinessSettings} element={<LazyPage page={<BusinessSettings />} />} />
        {/* Online Estimator / Journeys -- every route requires the `polaris` app. */}
        <Route element={<RequireApp clientId="polaris" />}>
          <Route path={workflowLanding} element={<LazyPage page={<WorkflowLanding />} />} />
          <Route path={`${workflowLanding}/folder/:id`} element={<LazyPage page={<FolderDetailPage />} />} />
          <Route path={`${workflowLanding}/create`} element={<LazyPage page={<CreateWorkflow />} />} />
          <Route path={`${workflowLanding}/:id`} element={<LazyPage page={<WorkflowEditorLanding />} />}>
            <Route path="share" element={<LazyPage page={<EmbeddedLanding />} />} />
            <Route path="tracking" element={<LazyPage page={<WorkflowTracking />} />} />
            <Route path="analytics" element={<LazyPage page={<WorkflowAnalytics />} />} />
            <Route path="theme" element={<LazyPage page={<WorkflowThemeTab />} />} />
            <Route path="send-email" element={<LazyPage page={<WorkflowSendEmail />} />} />
            <Route path="send-email/history" element={<LazyPage page={<WorkflowSendHistory />} />} />
            <Route path="step/:stepTrackingId?" element={<LazyPage page={<WorkflowEditor />} />} />
          </Route>
          <Route path={settings} element={<Navigate to={defaultSettings('system')} replace />} />
          <Route path={`${settings}/:tab`} element={<LazyPage page={<SettingLanding />} />}>
            <Route index element={<LazyPage page={<Settings />} />} />
            <Route path="locations" element={<LazyPage page={<TerritoriesListing />} />} />
            <Route path="locations/:id" element={<LazyPage page={<TerritoryLanding />} />} />
          </Route>
        </Route>
        <Route path={heroSettings} element={<LazyPage page={<Main />} />}>
          {/*
            Canvassing settings require `hero`. Members/Teams below stay ungated -- they are
            org-wide user administration (already role-gated), and MemberProfileForm is the only
            UI that can grant a user their apps, so gating it would be a self-lockout.
          */}
          <Route element={<RequireApp clientId="hero" />}>
            <Route element={<LazyPage page={<HeroSettingsLanding />} />}>
              <Route index element={<LazyPage page={<HeroDefaultSettings />} />} />
              <Route
                path="locations"
                element={
                  <LazyPage
                    page={
                      <TerritoriesListing
                        getDetailRoute={getHeroLocationSettingRoute}
                        createRoute={getHeroLocationSettingRoute()}
                      />
                    }
                  />
                }
              />
              <Route path="locations/:id" element={<LazyPage page={<HeroTerritorySettings />} />} />
            </Route>
          </Route>
          <Route path="members" element={<LazyPage page={<Members />} />} />
          <Route path="members/:id" element={<LazyPage page={<MemberProfile />} />} />
          <Route path="teams" element={<LazyPage page={<Teams />} />} />
          <Route path="teams/:id" element={<LazyPage page={<TeamManagement />} />} />
        </Route>
        <Route path="companies" element={<LazyPage page={<CompanyListing />} />} />
        <Route path="companies/:id/subscriptions" element={<LazyPage page={<CompanySubscriptions />} />} />
        <Route path="company" element={<LazyPage page={<Main />} />}>
          <Route path="settings" element={<LazyPage page={<CompanySettings />} />}>
            <Route index element={<Navigate to={companyDetails} replace />} />
            <Route path="details" element={<LazyPage page={<CompanyDetails />} />} />
            <Route path="apiKeys" element={<LazyPage page={<CompanyApiKeys />} />} />
            <Route path="webhooks/incoming" element={<LazyPage page={<IncomingWebhooks />} />} />
            <Route path="webhooks/outgoing" element={<LazyPage page={<OutgoingWebhooks />} />} />
            <Route path="plan" element={<LazyPage page={<CompanyPlan />} />} />
          </Route>
        </Route>
        <Route path="marketing" element={<LazyPage page={<MarketingResources />} />} />
        {/*
          Without this guard an unentitled direct link dead-ends at the SSO failure screen inside
          SsoIframeShell, with no explanation of why.
        */}
        <Route element={<RequireApp clientId="presentations" />}>
          <Route path={presentations}>
            {/* Bare /presentations renders my-presentations anyway; make the URL say so,
                otherwise no sidebar item highlights and the section stays collapsed. */}
            <Route index element={<Navigate to={presentationsDefaultPath} replace />} />
            <Route path="*" element={<LazyPage page={<Presentations />} />} />
          </Route>
        </Route>

        {/* Placeholder routes for new nav items */}
        <Route path="dashboard" element={<LazyPage page={<PlaceholderPage title="Dashboard" />} />} />
        <Route path={miaAdCopywriter} element={<LazyPage page={<AdCopywriter />} />} />
        {/*
          ENG-1301: we no longer sell Ace, so every /ace route sits behind the `ace` flag, which is
          off for everyone -- a bookmark or a typed URL now redirects instead of rendering. The
          coming-soon teaser moves under the same gate: nothing links to it any more, and keeping it
          here means one flag flip restores the whole surface. The pages themselves are untouched.
        */}
        <Route element={<RequireFeatureFlag flag="ace" />}>
          <Route path={aceToday} element={<LazyPage page={<PlaceholderPage title="Ace — Today" />} />} />
          <Route
            path={aceConversations}
            element={<LazyPage page={<PlaceholderPage title="Ace — Conversations" />} />}
          />
          <Route
            path={aceAutoFollowUp}
            element={<LazyPage page={<PlaceholderPage title="Ace — Auto Follow-up" />} />}
          />
          <Route path={aceTeach} element={<LazyPage page={<PlaceholderPage title="Ace — Teach Ace" />} />} />
          <Route path={aceSettings} element={<LazyPage page={<PlaceholderPage title="Ace — Settings" />} />} />
          <Route path={aceComingSoon} element={<LazyPage page={<AceComingSoon />} />} />
        </Route>
        <Route path={miaToday} element={<LazyPage page={<PlaceholderPage title="Mia — Today" />} />} />
        <Route path={miaMetaAds} element={<LazyPage page={<PlaceholderPage title="Mia — Meta Ads" />} />} />
        {/*
          ENG-1127: AI Visibility rolls out behind the `canonry` flag. Gating the route keeps a
          bookmark or a typed URL covered, not just the nav entry, and a flagged-out visitor never
          mounts the dashboard -- so its workspace query never fires.
        */}
        <Route element={<RequireFeatureFlag flag="canonry" />}>
          <Route path={miaCanonryDashboard} element={<LazyPage page={<CanonryDashboard />} />} />
        </Route>
        <Route path={miaGoogleAiSearch} element={<LazyPage page={<GoogleAiSearch />} />} />
        <Route path={miaTeach} element={<LazyPage page={<PlaceholderPage title="Mia — Teach Mia" />} />} />
        <Route path={miaSettings} element={<LazyPage page={<PlaceholderPage title="Mia — Settings" />} />} />
        <Route path={calendar} element={<LazyPage page={<CalendarSettingsLanding />} />}>
          <Route index element={<LazyPage page={<CalendarDefaultSettings />} />} />
          <Route
            path="locations"
            element={
              <LazyPage
                page={
                  <TerritoriesListing
                    getDetailRoute={getCalendarLocationRoute}
                    createRoute={getCalendarLocationRoute()}
                  />
                }
              />
            }
          />
          <Route path="locations/:id" element={<LazyPage page={<CalendarTerritorySettings />} />} />
        </Route>
        <Route path="profile" element={<LazyPage page={<ProfileLanding />} />}>
          <Route path="details" element={<LazyPage page={<ProfileSettings />} />} />
          <Route path="changePassword" element={<LazyPage page={<ChangePassword />} />} />
        </Route>
        <Route path={productAnalyticsDashboard} element={<LazyPage page={<ProductAnalyticsDashboard />} />} />
        <Route path={plansLanding} element={<LazyPage page={<PlanLanding />} />} />
      </Route>
      <Route path={loginRoute} element={<LazyPage page={<Login />} />} errorElement={<ErrorPage />} />
      <Route path={newPassword} element={<LazyPage page={<NewPassword />} />} errorElement={<ErrorPage />} />
      <Route path={resetPasswordRoute} element={<LazyPage page={<ResetPassword />} />} errorElement={<ErrorPage />} />
      <Route path={emailSentRoute} element={<LazyPage page={<ResetPasswordSent />} />} errorElement={<ErrorPage />} />
      <Route path="*" element={<LazyPage page={<NotFound />} />} errorElement={<ErrorPage />} />
    </Route>
  )
);

export const App = (): JSX.Element => {
  return (
    <ThemeProvider defaultTheme="light" disableTransitionOnChange>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
